import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import CustomDropdown, { DropdownOption } from '../UI/CustomDropdown';
import sidePanelStyles from '../../styles/SidePanel.module.css';
import buttonStyles from '../../styles/Buttons.module.css';

interface FacultyEnrollmentFormData {
    facultyName: string;
    email: string;
    phoneNumber: string;
    department: string;
    designation: string;
}

const designationOptions: DropdownOption[] = [
    { value: 'chairman', label: 'Chairman' },
    { value: 'vice_chairman', label: 'Vice Chairman' },
    { value: 'director', label: 'Director' },
    { value: 'principal', label: 'Principal' },
    { value: 'vice_principal', label: 'Vice Principal' },
    { value: 'dean', label: 'Dean' },
    { value: 'head_of_department', label: 'Head of Department' },
    { value: 'professor', label: 'Professor' },
    { value: 'associate_professor', label: 'Associate Professor' },
    { value: 'assistant_professor', label: 'Assistant Professor' },
    { value: 'senior_assistant', label: 'Senior Assistant' },
    { value: 'junior_assistant', label: 'Junior Assistant' },
    { value: 'grade2_technician', label: 'Grade 2 Technician' },
];

const departmentOptions: DropdownOption[] = [
    { value: 'CSE', label: 'CSE' },
    { value: 'ECE', label: 'ECE' },
    { value: 'EEE', label: 'EEE' },
    { value: 'ME', label: 'ME' },
    { value: 'CS & SE', label: 'CS & SE' },
    { value: 'CSIT', label: 'CSIT' },
    { value: 'CSE(AI&ML)', label: 'CSE(AI&ML)' },
    { value: 'S&H', label: 'S&H' },
];

interface FacultyEnrollmentFormProps {
    onSubmit: (data: FacultyEnrollmentFormData) => void;
    onCancel?: () => void;
    isLoading?: boolean;
}

const FacultyEnrollmentForm: React.FC<FacultyEnrollmentFormProps> = ({ onSubmit, onCancel, isLoading = false }) => {
    const { control, register, handleSubmit, formState: { errors } } = useForm<FacultyEnrollmentFormData>();

    const handleFormSubmit = (data: FacultyEnrollmentFormData) => {
        onSubmit(data);
    };

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)} className={sidePanelStyles.form}>
            <div className={sidePanelStyles.sectionHeader} style={{ marginTop: '0' }}>
            </div>

            {/* Faculty Name */}
            <div className={sidePanelStyles.formGroup}>
                <label className={sidePanelStyles.formLabel}>
                    Faculty Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                    type="text"
                    {...register('facultyName', { required: 'Faculty Name is required' })}
                    className={sidePanelStyles.formControl}
                    placeholder="Enter Faculty Name"
                    disabled={isLoading}
                />
                {errors.facultyName && (
                    <span style={{ color: 'red', fontSize: 'var(--text-xs)' }}>{errors.facultyName.message}</span>
                )}
            </div>

            {/* Email */}
            <div className={sidePanelStyles.formGroup}>
                <label className={sidePanelStyles.formLabel}>
                    Email <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                    type="email"
                    {...register('email', {
                        required: 'Email is required',
                        pattern: {
                            value: /^[a-zA-Z0-9._%+-]+@lendi\.edu\.in$/,
                            message: 'Email must be a valid @lendi.edu.in address'
                        }
                    })}
                    className={sidePanelStyles.formControl}
                    placeholder="@lendi.edu.in"
                    disabled={isLoading}
                />
                {errors.email && (
                    <span style={{ color: 'red', fontSize: 'var(--text-xs)' }}>{errors.email.message}</span>
                )}
            </div>

            {/* Phone Number */}
            <div className={sidePanelStyles.formGroup}>
                <label className={sidePanelStyles.formLabel}>
                    Phone Number <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                    type="tel"
                    {...register('phoneNumber', {
                        required: 'Phone Number is required',
                        pattern: {
                            value: /^\d{10}$/, // Basic 10-digit validation
                            message: 'Please enter a valid 10-digit phone number'
                        }
                    })}
                    className={sidePanelStyles.formControl}
                    placeholder="Enter Phone Number"
                    disabled={isLoading}
                />
                {errors.phoneNumber && (
                    <span style={{ color: 'red', fontSize: 'var(--text-xs)' }}>{errors.phoneNumber.message}</span>
                )}
            </div>

            {/* Department */}
            <div className={sidePanelStyles.formGroup}>
                <label className={sidePanelStyles.formLabel}>
                    Department <span style={{ color: 'red' }}>*</span>
                </label>
                <Controller
                    name="department"
                    control={control}
                    rules={{ required: 'Department is required' }}
                    render={({ field }) => (
                        <CustomDropdown
                            options={departmentOptions}
                            value={field.value || ''}
                            onChange={(value) => field.onChange(Array.isArray(value) ? value[0] : value)}
                            placeholder="Select Department"
                            size="full"
                            variant="outlined"
                            disabled={isLoading}
                        />
                    )}
                />
                {errors.department && (
                    <span style={{ color: 'red', fontSize: 'var(--text-xs)' }}>{errors.department.message}</span>
                )}
            </div>

            {/* Designation */}
            <div className={sidePanelStyles.formGroup}>
                <label className={sidePanelStyles.formLabel}>
                    Designation <span style={{ color: 'red' }}>*</span>
                </label>
                <Controller
                    name="designation"
                    control={control}
                    rules={{ required: 'Designation is required' }}
                    render={({ field }) => (
                        <CustomDropdown
                            options={designationOptions}
                            value={field.value || ''}
                            onChange={(value) => field.onChange(Array.isArray(value) ? value[0] : value)}
                            placeholder="Select Designation"
                            size="full"
                            variant="outlined"
                            disabled={isLoading}
                        />
                    )}
                />
                {errors.designation && (
                    <span style={{ color: 'red', fontSize: 'var(--text-xs)' }}>{errors.designation.message}</span>
                )}
            </div>

            <div className={sidePanelStyles.formActions}>
                {onCancel && (
                    <button type="button" className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`} onClick={onCancel} disabled={isLoading}>
                        Cancel
                    </button>
                )}
                <button type="submit" className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`} disabled={isLoading}>
                    {isLoading ? 'Submitting...' : 'Submit Enrollment'}
                </button>
            </div>
        </form>
    );
};

export default FacultyEnrollmentForm;